/**
 * <hero-field>: the home hero's session field. Simulated intraday paths fan out from the 09:30 open toward a
 * same-day expiry between two labelled GEX levels: the call wall above and the put wall below, with dashed minor
 * levels between. A lit cone shows the paths' own spread (its two-sigma edges land on the walls at expiry), one
 * path is highlighted with a bright head, and the session ruler fills along the colour ramp as the clock runs.
 *
 * Nothing is drawn behind the copy: the paths fade in just past the measured edge of the headline and lede, and
 * the levels start right of it. The field ends at the content edge. It pauses offscreen, in background tabs and
 * under reduced motion, where it holds a static frame at 14:42 ET. Colours come from the CSS tokens, so it
 * follows the theme. Synthetic paths only; no market data.
 *
 * Smoothness: everything that does not move is drawn once into offscreen layers (the levels and tags, the cone,
 * the forecast edges, and the whole session's fan of paths). A frame only reveals those layers up to "now" with a
 * clip at a fractional x, then draws the few marks that ride "now" (head, front light, ruler, clock). The reveal
 * is continuous, so the paths glide instead of stepping two simulated minutes at a time.
 */

// Dependencies
import { AnJSElement, html, registerComponent } from 'almostnojs';
import { reducedMotion } from './enhanced.js';

// Model: 56 paths of 195 two-minute steps (6.5 hours), walls and minor levels in field units.
const PATHS = 56, STEPS = 195, CALL = 0.58, PUT = -0.62, MINOR = [0.24, -0.28];

// Per-step spread of the cone, tied to the walls: the two-sigma edges reach them at expiry (put side wider).
const SDU = CALL / 2 / Math.sqrt(STEPS), SDD = -PUT / 2 / Math.sqrt(STEPS);

// Timing: draw the session, hold it, fade, then start a new one. The static frame sits at 14:42 ET.
const DRAW_MS = 5200, HOLD_MS = 5600, FADE_MS = 900, CYCLE_MS = DRAW_MS + HOLD_MS + FADE_MS, STATIC = 0.8;

// Stacked layout below this viewport width (matches the stylesheet's hero breakpoint).
const NARROW = '(max-width: 900px)';

// Text on the field.
const MONO = '500 10.5px "JetBrains Mono", ui-monospace, monospace';

/**
 * Standard normal draw (Box-Muller).
 *
 * @returns {number} Sample.
 */
function gauss() {

    // Two uniforms in (0, 1).
    let u = 0, v = 0;
    while (!u) u = Math.random();
    while (!v) v = Math.random();

    // Sample.
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/**
 * Ease-in-out (sine): a gentle start and a gentle landing, so the reveal never lurches.
 *
 * @param {number} t - 0..1.
 * @returns {number} Eased 0..1.
 */
const ease = (t) => 0.5 - 0.5 * Math.cos(Math.PI * t);

/**
 * The time (0..1) at which the easing reaches a given progress.
 *
 * @param {number} p - Progress 0..1.
 * @returns {number} Time 0..1.
 */
const unease = (p) => Math.acos(1 - 2 * p) / Math.PI;

/**
 * A hex colour with alpha, as rgba().
 *
 * @param {string} hex - #rrggbb.
 * @param {number} a - Alpha.
 * @returns {string} rgba() string.
 */
function rgba(hex, a) {

    // Channels.
    const n = Number.parseInt(hex.replace('#', ''), 16);

    // Colour.
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

/**
 * A new offscreen layer.
 *
 * @returns {{canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D}} Layer.
 */
function layer() {

    // Canvas and context.
    const canvas = document.createElement('canvas');
    return { canvas, ctx: canvas.getContext('2d') };
}

/**
 * The session field.
 */
class HeroField extends AnJSElement {

    /**
     * One canvas; everything else is drawn into it.
     *
     * @returns {TemplateResult} Template.
     */
    render() {

        // Canvas.
        return html`<canvas></canvas>`;
    }

    /**
     * Set up the canvas, the layers, the model and every pause condition.
     *
     * @returns {void}
     */
    init() {

        // Visible canvas and the cached layers.
        this.canvas = this.querySelector('canvas');
        this.ctx = this.canvas.getContext('2d');
        this.layers = { still: layer(), cone: layer(), edges: layer(), fan: layer() };

        // Loop state.
        this.raf = 0;
        this.start = 0;
        this.inView = true;
        this.narrow = window.matchMedia(NARROW);
        this.reduce = reducedMotion();

        // First session, geometry and colours, then the static frame until the loop takes over.
        this.generate();
        this.readPalette();
        this.measure();
        this.draw(STATIC, 1, false);
        this.run();

        // Rebuild on size, theme and font changes.
        const redraw = () => { this.measure(); if (!this.raf) this.draw(STATIC, 1, false); };
        const retheme = () => { this.readPalette(); this.paintLayers(); if (!this.raf) this.draw(STATIC, 1, false); };
        this.resizer = new ResizeObserver(redraw);
        this.resizer.observe(this);
        this.own(() => this.resizer.disconnect());
        if (document.fonts?.ready) document.fonts.ready.then(redraw);
        document.addEventListener('themechange', retheme);
        this.own(() => document.removeEventListener('themechange', retheme));

        // Device pixel ratio changes (another screen, browser zoom): re-measure the backing stores.
        const watchDensity = () => {
            const query = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
            const changed = () => { query.removeEventListener('change', changed); redraw(); watchDensity(); };
            query.addEventListener('change', changed);
            this.own(() => query.removeEventListener('change', changed));
        };
        watchDensity();

        // Pause offscreen, in background tabs and under reduced motion.
        if ('IntersectionObserver' in window) {
            this.viewer = new IntersectionObserver((entries) => { this.inView = entries[0].isIntersecting; this.run(); });
            this.viewer.observe(this);
            this.own(() => this.viewer.disconnect());
        }
        const resume = () => this.run();
        document.addEventListener('visibilitychange', resume);
        this.own(() => document.removeEventListener('visibilitychange', resume));
        this.reduce.addEventListener('change', resume);
        this.own(() => this.reduce.removeEventListener('change', resume));
    }

    /**
     * Stop the loop when the element leaves the page.
     *
     * @returns {void}
     */
    destroy() {

        // No more frames.
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = 0;
    }

    /**
     * Simulate a new session: the paths, each path's colour (its level at the close), and the highlighted path.
     *
     * @returns {void}
     */
    generate() {

        // Random walks with a per-path volatility (mean 0.072 per hour).
        this.paths = [];
        for (let p = 0; p < PATHS; p++) {
            const pts = new Float32Array(STEPS + 1), vol = 0.055 + Math.random() * 0.034;
            for (let i = 1, y = 0; i <= STEPS; i++) {
                y = Math.max(-1.15, Math.min(1.15, y + vol * gauss() * Math.sqrt(1 / 12)));
                pts[i] = y;
            }
            this.paths.push(pts);
        }

        // Colour class 1..5 on the ramp: 0 at the lower two-sigma edge at the close, 1 at the upper.
        this.classes = this.paths.map((pts) => {
            const v = pts[STEPS], sd = v >= 0 ? SDU : SDD, t = 0.5 + 0.5 * v / (2 * sd * Math.sqrt(STEPS));
            return 1 + Math.max(0, Math.min(4, Math.floor(t * 5)));
        });

        // Highlight: ends nearest +0.24, stays inside ±0.5, and moves the most.
        let best = Infinity;
        this.highlight = 0;
        this.paths.forEach((pts, k) => {
            let lo = 0, hi = 0, mx = 0;
            for (let i = 0; i <= STEPS; i++) { lo = Math.min(lo, pts[i]); hi = Math.max(hi, pts[i]); mx = Math.max(mx, Math.abs(pts[i])); }
            const score = Math.abs(pts[STEPS] - 0.24) - (hi - lo) * 0.2 + (mx > 0.5 ? 9 : 0);
            if (score < best) { best = score; this.highlight = k; }
        });
    }

    /**
     * Size the canvases to the box, measure the copy's right edge, derive the geometry and repaint the layers.
     *
     * @returns {void}
     */
    measure() {

        // Box and device pixels (capped at 2x).
        const rect = this.getBoundingClientRect(), dpr = Math.min(window.devicePixelRatio || 1, 2);
        this.w = rect.width;
        this.h = rect.height;
        this.dpr = dpr;
        for (const { canvas, ctx } of [{ canvas: this.canvas, ctx: this.ctx }, ...Object.values(this.layers)]) {
            canvas.width = Math.max(1, Math.round(this.w * dpr));
            canvas.height = Math.max(1, Math.round(this.h * dpr));
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }

        // Right edge of the eyebrow, headline and lede, measured on their rendered lines.
        this.copyRight = 0;
        const stage = this.closest('.hero-stage');
        stage?.querySelectorAll('.hero-copy > h1, .hero-copy > .lede, .hero-copy > .eyebrow').forEach((el) => {
            const range = document.createRange();
            range.selectNodeContents(el);
            for (const line of range.getClientRects()) if (line.width) this.copyRight = Math.max(this.copyRight, line.right - rect.left);
        });

        // Geometry shared by the layers and the frames.
        const w = this.w, h = this.h, narrow = this.narrow.matches;
        const x0 = narrow ? 0 : w * 0.30, x1 = w;
        const axisY = Math.round(h - (narrow ? 38 : 48)) + 0.5;
        const top = narrow ? 18 : 30, bottom = axisY - (narrow ? 26 : 40);
        const yMid = (top + bottom) / 2, yAmp = (bottom - top) * (narrow ? 0.44 : 0.40);
        const fadeA = narrow ? 0 : this.copyRight + 8, fadeB = narrow ? w * 0.34 : this.copyRight + 132;
        const lx0 = narrow ? 0 : this.copyRight + 44;
        this.g = {
            narrow, x0, x1, axisY, top, bottom, yMid, yAmp, fadeA, fadeB, lx0,
            lx1: lx0 + Math.min(180, (x1 - lx0) * 0.3),
            fadeFrom: narrow ? 0.22 : 0,
            stepX: (x1 - x0) / STEPS,
            toY: (v) => yMid - v * yAmp,
        };

        // Everything still.
        this.paintLayers();
    }

    /**
     * Read the colour tokens (after load and on every theme change).
     *
     * @returns {void}
     */
    readPalette() {

        // Computed tokens, inherited by this element.
        const style = getComputedStyle(this);
        const token = (name, fallback) => style.getPropertyValue(name).trim() || fallback;

        // Palette.
        this.c = {
            r: [null, token('--r1', '#2b6a8a'), token('--r2', '#14857f'), token('--r3', '#21966b'), token('--r4', '#56a65e'), token('--r5', '#d39a2b')],
            accent: token('--accent', '#0f5c4d'),
            accent2: token('--accent-2', '#1a8a69'),
            head: token('--head', '#0f5c4d'),
            muted: token('--muted', '#575e69'),
            ink: token('--ink', '#0e1116'),
            ink2: token('--ink-2', '#2b313b'),
            tag: token('--tag-bg', '#fbfaf7'),
            rule: token('--rule', '#dcd8cd'),
            base: token('--hero-base', '#f7f6f2'),
            pathA: Number.parseFloat(token('--path-a', '0.34')),
            coneA: Number.parseFloat(token('--cone-a', '0.085')),
        };
    }

    /**
     * Keep a layer's content only where a horizontal alpha mask allows.
     *
     * @param {CanvasRenderingContext2D} cx - Layer context.
     * @param {Array<[number, number]>} stops - [x, alpha] stops.
     * @returns {void}
     */
    mask(cx, stops) {

        // Gradient across the field.
        const gradient = cx.createLinearGradient(0, 0, this.w, 0);
        for (const [x, a] of stops) gradient.addColorStop(Math.max(0, Math.min(1, x / this.w)), `rgba(0, 0, 0, ${a})`);

        // Keep the content where the gradient is opaque.
        cx.save();
        cx.globalCompositeOperation = 'destination-in';
        cx.fillStyle = gradient;
        cx.fillRect(0, 0, this.w, this.h);
        cx.restore();
    }

    /**
     * Paint every cached layer: the still marks, the cone, the forecast edges and the session's fan.
     *
     * @returns {void}
     */
    paintLayers() {

        // Not measured or themed yet.
        if (!this.g || !this.c || !this.w) return;
        const { c, g } = this;
        const { x0, x1, axisY, toY, lx0, lx1 } = g;
        const yC = Math.round(toY(CALL)) + 0.5, yP = Math.round(toY(PUT)) + 0.5;
        const clear = (cx) => { cx.save(); cx.setTransform(1, 0, 0, 1, 0, 0); cx.clearRect(0, 0, cx.canvas.width, cx.canvas.height); cx.restore(); };

        // Still: the levels (from right of the copy to the content edge), the wall tags and the ruler's track.
        const still = this.layers.still.ctx;
        clear(still);
        for (const [y, colour] of [[yC, c.r[5]], [yP, c.r[1]]]) {
            const band = still.createLinearGradient(0, y - 10, 0, y + 10);
            band.addColorStop(0, rgba(colour, 0));
            band.addColorStop(0.5, rgba(colour, 0.16));
            band.addColorStop(1, rgba(colour, 0));
            still.fillStyle = band;
            still.fillRect(lx0, y - 10, x1 - lx0, 20);
        }
        still.lineWidth = 1;
        still.strokeStyle = rgba(c.muted, 0.4);
        still.setLineDash([2, 5]);
        for (const v of MINOR) { const y = Math.round(toY(v)) + 0.5; still.beginPath(); still.moveTo(lx0, y); still.lineTo(x1, y); still.stroke(); }
        still.setLineDash([]);
        still.lineWidth = 1.2;
        for (const [y, colour] of [[yC, c.r[5]], [yP, c.r[1]]]) { still.strokeStyle = colour; still.beginPath(); still.moveTo(lx0, y); still.lineTo(x1, y); still.stroke(); }
        this.mask(still, [[lx0, 0], [lx1, 1]]);
        still.font = MONO;
        if ('letterSpacing' in still) still.letterSpacing = '1.26px';
        for (const [label, y, colour] of [['CALL WALL', yC - 28, c.r[5]], ['PUT WALL', yP + 8, c.r[1]]]) {
            const tw = Math.ceil(still.measureText(label).width) + 34, x = x1 - tw - 0.5;
            still.fillStyle = c.tag;
            still.strokeStyle = rgba(colour, 0.55);
            still.lineWidth = 1;
            still.beginPath(); still.roundRect(x, y + 0.5, tw, 20, 4); still.fill(); still.stroke();
            still.fillStyle = colour;
            still.beginPath(); still.roundRect(x + 8, y + 9.5, 10, 2, 1); still.fill();
            still.fillStyle = c.ink2;
            still.fillText(label, x + 24, y + 14.1);
        }
        still.strokeStyle = rgba(c.muted, 0.28);
        still.lineWidth = 1;
        still.beginPath(); still.moveTo(x0, axisY); still.lineTo(x1, axisY); still.stroke();
        still.strokeStyle = rgba(c.muted, 0.45);
        for (let m = 0; m <= 390; m += 30) {
            const tx = Math.round(x0 + (m / 390) * (x1 - x0)) + 0.5, big = m % 60 === 30 || m === 0 || m === 390;
            still.beginPath(); still.moveTo(tx, axisY - (big ? 5 : 3)); still.lineTo(tx, axisY - 1); still.stroke();
        }
        still.fillStyle = rgba(c.r[5], 0.2);
        still.beginPath(); still.arc(x1 - 6, axisY, 6, 0, Math.PI * 2); still.fill();
        still.strokeStyle = c.r[5];
        still.lineWidth = 1.4;
        still.beginPath(); still.arc(x1 - 6, axisY, 2.6, 0, Math.PI * 2); still.stroke();
        still.fillStyle = c.muted;
        still.textAlign = 'left';
        still.fillText('09:30 ET', x0, axisY + 24);

        // Cone: one and two sigma, the ramp top to bottom, faded in past the copy.
        const cone = this.layers.cone.ctx;
        clear(cone);
        const ramp = cone.createLinearGradient(0, toY(CALL), 0, toY(PUT));
        [[0, 5], [0.28, 4], [0.5, 3], [0.72, 2], [1, 1]].forEach(([o, k]) => ramp.addColorStop(o, c.r[k]));
        for (const [k, a] of [[2, c.coneA], [1, c.coneA * 1.1]]) {
            cone.beginPath();
            for (let i = 0; i <= STEPS; i += 3) cone.lineTo(x0 + i * g.stepX, toY(k * SDU * Math.sqrt(i)));
            cone.lineTo(x1, toY(k * SDU * Math.sqrt(STEPS)));
            cone.lineTo(x1, toY(-k * SDD * Math.sqrt(STEPS)));
            for (let i = STEPS - (STEPS % 3); i >= 0; i -= 3) cone.lineTo(x0 + i * g.stepX, toY(-k * SDD * Math.sqrt(i)));
            cone.closePath();
            cone.globalAlpha = a;
            cone.fillStyle = ramp;
            cone.fill();
        }
        cone.globalAlpha = 1;
        this.mask(cone, [[g.fadeA, g.fadeFrom], [g.fadeB, 1]]);

        // Forecast: the two-sigma edges running out to the walls at expiry (revealed from "now" onward).
        const edges = this.layers.edges.ctx;
        clear(edges);
        edges.lineWidth = 1.2;
        edges.lineCap = 'round';
        edges.setLineDash([1, 4]);
        for (const [sd, sign, colour] of [[SDU, 1, c.r[5]], [SDD, -1, c.r[1]]]) {
            edges.strokeStyle = rgba(colour, 0.75);
            edges.beginPath();
            for (let i = 0; i <= STEPS; i += 3) edges.lineTo(x0 + i * g.stepX, toY(sign * 2 * sd * Math.sqrt(i)));
            edges.lineTo(x1, toY(sign * 2 * sd * Math.sqrt(STEPS)));
            edges.stroke();
        }
        edges.setLineDash([]);
        for (const [y, colour] of [[yC, c.r[5]], [yP, c.r[1]]]) { edges.fillStyle = colour; edges.beginPath(); edges.arc(x1 - 1, y, 2.4, 0, Math.PI * 2); edges.fill(); }
        this.mask(edges, [[lx0, 0], [lx1, 1]]);

        // Ruler fill gradient, reused every frame.
        this.rulerRamp = this.ctx.createLinearGradient(x0, 0, x1, 0);
        [[0, 1], [0.27, 2], [0.52, 3], [0.76, 4], [1, 5]].forEach(([o, k]) => this.rulerRamp.addColorStop(o, c.r[k]));

        // The session's paths.
        this.paintFan();
    }

    /**
     * Paint the whole session's fan and highlighted path into their layer (revealed up to "now" each frame).
     *
     * @returns {void}
     */
    paintFan() {

        // Not ready.
        if (!this.g || !this.c) return;
        const { c, g } = this;
        const fan = this.layers.fan.ctx;
        fan.save();
        fan.setTransform(1, 0, 0, 1, 0, 0);
        fan.clearRect(0, 0, fan.canvas.width, fan.canvas.height);
        fan.restore();

        // One path, start to close.
        const trace = (pts) => { fan.beginPath(); fan.moveTo(g.x0, g.toY(0)); for (let i = 1; i <= STEPS; i++) fan.lineTo(g.x0 + i * g.stepX, g.toY(pts[i])); };

        // The fan, coloured by level at the close.
        fan.lineJoin = 'round';
        fan.lineCap = 'round';
        fan.lineWidth = 1;
        this.paths.forEach((pts, p) => {
            if (p === this.highlight) return;
            fan.strokeStyle = rgba(c.r[this.classes[p]], c.pathA);
            trace(pts);
            fan.stroke();
        });

        // The highlighted path: a soft halo, then the line warming toward its head, with a glow.
        const hp = this.paths[this.highlight];
        fan.strokeStyle = rgba(c.accent2, 0.14);
        fan.lineWidth = 7;
        trace(hp);
        fan.stroke();
        const line = fan.createLinearGradient(g.fadeA, 0, g.x1, 0);
        line.addColorStop(0, c.r[2]);
        line.addColorStop(0.55, c.accent2);
        line.addColorStop(1, c.head);
        fan.save();
        fan.strokeStyle = line;
        fan.lineWidth = 1.9;
        fan.shadowBlur = 6;
        fan.shadowColor = rgba(c.accent2, 0.5);
        trace(hp);
        fan.stroke();
        fan.restore();

        // Faded in past the copy.
        this.mask(fan, [[g.fadeA, g.fadeFrom], [g.fadeB, 1]]);
    }

    /**
     * Whether the loop should be running.
     *
     * @returns {boolean} Running.
     */
    active() {

        // On screen, visible tab, motion allowed.
        return this.inView && document.visibilityState === 'visible' && !this.reduce.matches;
    }

    /**
     * Start (or stop) the loop to match the pause conditions.
     *
     * @returns {void}
     */
    run() {

        // Paused: hold the static frame.
        if (!this.active()) {
            if (this.raf) cancelAnimationFrame(this.raf);
            this.raf = 0;
            this.draw(STATIC, 1, false);
            return;
        }

        // Already running.
        if (this.raf) return;

        // Pick up from the static frame, so the first animated frame continues it.
        this.raf = requestAnimationFrame((now) => {
            this.start = now - unease(STATIC) * DRAW_MS;
            this.frame(now);
        });
    }

    /**
     * One animation frame.
     *
     * @param {number} now - rAF time.
     * @returns {void}
     */
    frame(now) {

        // Stopped meanwhile.
        if (!this.active()) { this.raf = 0; this.draw(STATIC, 1, false); return; }

        // Where in the cycle; a new session is painted while the field is fully faded out.
        const t = now - this.start;
        if (t < DRAW_MS) this.draw(ease(t / DRAW_MS), 1, true, now);
        else if (t < DRAW_MS + HOLD_MS) this.draw(1, 1, true, now);
        else if (t < CYCLE_MS) this.draw(1, 1 - (t - DRAW_MS - HOLD_MS) / FADE_MS, true, now);
        else { this.generate(); this.paintFan(); this.start = now; this.draw(0, 1, true, now); }

        // Next.
        this.raf = requestAnimationFrame((next) => this.frame(next));
    }

    /**
     * Copy a layer onto the canvas, clipped to a horizontal band.
     *
     * @param {Object} source - Layer.
     * @param {number} from - Left edge (CSS px).
     * @param {number} to - Right edge (CSS px).
     * @returns {void}
     */
    reveal(source, from, to) {

        // Nothing to show.
        if (to <= from) return;

        // Clip and copy.
        const { ctx } = this;
        ctx.save();
        ctx.beginPath();
        ctx.rect(from, 0, to - from, this.h);
        ctx.clip();
        ctx.drawImage(source.canvas, 0, 0, this.w, this.h);
        ctx.restore();
    }

    /**
     * Draw one frame.
     *
     * @param {number} progress - Session progress, 0 (09:30) to 1 (16:00).
     * @param {number} alpha - Overall opacity (fades between sessions).
     * @param {boolean} animating - Whether the loop is running (the marker breathes).
     * @param {number} [now] - rAF time.
     * @returns {void}
     */
    draw(progress, alpha, animating, now = 0) {

        // Nothing to draw into yet.
        const { ctx, c, g, w, h } = this;
        if (!w || !h || !c || !g) return;
        ctx.clearRect(0, 0, w, h);
        ctx.save();
        ctx.globalAlpha = alpha;

        // "Now", continuous: the fractional step and its x.
        const f = Math.max(0, Math.min(STEPS, progress * STEPS)), xNow = g.x0 + f * g.stepX;
        const nowA = g.narrow ? 1 : Math.max(0, Math.min(1, (xNow - g.fadeA) / (g.fadeB - g.fadeA)));
        const k = Math.floor(f), frac = f - k, hp = this.paths[this.highlight];
        const headY = g.toY(k >= STEPS ? hp[STEPS] : hp[k] + (hp[k + 1] - hp[k]) * frac);

        // 1. The cone: full strength up to "now", the forecast part dimmed.
        this.reveal(this.layers.cone, 0, xNow);
        ctx.globalAlpha = alpha * 0.5;
        this.reveal(this.layers.cone, xNow, w);
        ctx.globalAlpha = alpha;

        // 1b. The front light at "now".
        if (f > 0 && nowA > 0) {
            const cy = g.yMid + (SDD - SDU) * Math.sqrt(f) * g.yAmp / 2, ry = (SDU + SDD) * Math.sqrt(f) * g.yAmp + 28, rx = g.narrow ? 60 : 110;
            ctx.save();
            ctx.globalAlpha = alpha * nowA;
            ctx.translate(xNow, cy);
            ctx.scale(rx, ry);
            const light = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
            light.addColorStop(0, rgba(c.accent2, 0.30));
            light.addColorStop(0.55, rgba(c.accent2, 0.09));
            light.addColorStop(1, rgba(c.accent2, 0));
            ctx.fillStyle = light;
            ctx.beginPath();
            ctx.arc(0, 0, 1, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        }

        // 2. Levels, tags and the ruler's track.
        ctx.drawImage(this.layers.still.canvas, 0, 0, w, h);

        // 3. Forecast edges from "now" to expiry.
        if (f < STEPS) this.reveal(this.layers.edges, xNow, w);
        else this.reveal(this.layers.edges, w - 4, w);

        // 4. The "now" line.
        if (f > 0 && f < STEPS && nowA > 0) {
            ctx.strokeStyle = rgba(c.muted, 0.42 * nowA);
            ctx.lineWidth = 1;
            ctx.setLineDash([1, 4]);
            ctx.beginPath();
            ctx.moveTo(xNow, g.top - 6);
            ctx.lineTo(xNow, g.axisY - 9);
            ctx.stroke();
            ctx.setLineDash([]);
        }

        // 5–6. The fan and the highlighted path, revealed up to "now".
        this.reveal(this.layers.fan, 0, xNow);

        // 6b. The head, faded with the field so it never sits on the copy.
        if (f > 0 && nowA > 0) {
            ctx.save();
            ctx.globalAlpha = alpha * nowA;
            ctx.fillStyle = rgba(c.accent2, 0.08);
            ctx.beginPath(); ctx.arc(xNow, headY, 14, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = rgba(c.accent2, 0.22);
            ctx.beginPath(); ctx.arc(xNow, headY, 7, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = c.head;
            ctx.strokeStyle = c.base;
            ctx.lineWidth = 1.4;
            ctx.beginPath(); ctx.arc(xNow, headY, 3.4, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
            ctx.restore();
        }

        // 7. The ruler filling along the ramp to "now", and the marker.
        if (f > 0) {
            ctx.strokeStyle = this.rulerRamp;
            ctx.lineCap = 'round';
            ctx.globalAlpha = alpha * 0.18;
            ctx.lineWidth = 7;
            ctx.beginPath(); ctx.moveTo(g.x0, g.axisY); ctx.lineTo(xNow, g.axisY); ctx.stroke();
            ctx.globalAlpha = alpha;
            ctx.lineWidth = 2.2;
            ctx.beginPath(); ctx.moveTo(g.x0, g.axisY); ctx.lineTo(xNow, g.axisY); ctx.stroke();
        }
        const breathe = animating ? 10 + Math.sin((now / 2400) * Math.PI * 2) : 9;
        ctx.fillStyle = rgba(c.accent2, 0.18);
        ctx.beginPath(); ctx.arc(xNow, g.axisY, breathe, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = c.base;
        ctx.strokeStyle = c.accent2;
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(xNow, g.axisY, 4, 0, Math.PI * 2); ctx.fill(); ctx.stroke();

        // 8. The expiry label (lit once the session closes) and the running clock.
        const closed = progress >= 1, ty = g.axisY + 24;
        ctx.font = MONO;
        if ('letterSpacing' in ctx) ctx.letterSpacing = '1.26px';
        ctx.fillStyle = closed ? c.accent : c.ink2;
        ctx.textAlign = 'right';
        ctx.fillText(g.narrow ? '16:00' : '16:00 · EXPIRY', g.x1, ty);
        if (!closed) {
            const minutes = Math.floor(progress * 390), hh = 9 + Math.floor((30 + minutes) / 60), mm = (30 + minutes) % 60;
            const clock = `${hh < 10 ? '0' : ''}${hh}:${mm < 10 ? '0' : ''}${mm} ET`;
            const pw = g.narrow ? 70 : 78, cx = Math.max(g.x0 + (g.narrow ? 96 : 110), Math.min(g.x1 - (g.narrow ? 84 : 160), xNow));
            ctx.fillStyle = c.tag;
            ctx.strokeStyle = c.rule;
            ctx.lineWidth = 1;
            ctx.beginPath(); ctx.roundRect(cx - pw / 2, ty - 14.5, pw, 20, 10); ctx.fill(); ctx.stroke();
            if ('letterSpacing' in ctx) ctx.letterSpacing = '0.63px';
            ctx.fillStyle = c.ink;
            ctx.textAlign = 'center';
            ctx.fillText(clock, cx, ty);
        }

        // Done.
        ctx.restore();
    }
}

// Register
registerComponent('hero-field', HeroField);
